import type { TLRecord } from '@digitalsamba/tldraw';

export const SETTING_PREFIX = 'setting:';

const WHITEBOARD_MODES = ['dark', 'light'] as const;

export type WhiteboardMode = (typeof WHITEBOARD_MODES)[number];

export type WhiteboardSettingKey = 'mode';

export type WhiteboardSettings = {
  mode?: WhiteboardMode;
};

export type WhiteboardSettingId = `${typeof SETTING_PREFIX}${string}`;

export type WhiteboardSettingRecord = {
  id: WhiteboardSettingId;
  value: string;
};

export type WhiteboardRecord = TLRecord | WhiteboardSettingRecord;

export type WhiteboardRecordId = TLRecord['id'] | WhiteboardSettingId;

export type WhiteboardRecordUpdate = {
  shapeId: WhiteboardRecordId;
  data: string;
};

export type ParsedSetting = {
  key: WhiteboardSettingKey;
  value: string;
};

const ALLOWED_VALUES: Record<WhiteboardSettingKey, readonly string[]> = {
  mode: WHITEBOARD_MODES,
};

export const isSettingId = (id: unknown): id is WhiteboardSettingId =>
  typeof id === 'string' && id.startsWith(SETTING_PREFIX);

const settingKeyFromId = (id: string): WhiteboardSettingKey | null => {
  const key = id.slice(SETTING_PREFIX.length);

  return key in ALLOWED_VALUES ? (key as WhiteboardSettingKey) : null;
};

export const parseSetting = (record: unknown): ParsedSetting | null => {
  if (typeof record !== 'object' || record === null) {
    return null;
  }

  const { id, value } = record as { id?: unknown; value?: unknown };
  if (!isSettingId(id) || typeof value !== 'string') {
    return null;
  }

  const key = settingKeyFromId(id);
  if (!key || !ALLOWED_VALUES[key].includes(value)) {
    return null;
  }

  return { key, value };
};

export const isSettingRecord = (record: unknown): record is WhiteboardSettingRecord =>
  isSettingId((record as { id?: unknown } | null)?.id);

export const splitRecords = (records: WhiteboardRecord[]) => {
  const shapes: TLRecord[] = [];
  const settings: ParsedSetting[] = [];

  records.forEach((record) => {
    if (isSettingRecord(record)) {
      const parsed = parseSetting(record);
      if (parsed) {
        settings.push(parsed);
      }

      return;
    }

    shapes.push(record);
  });

  return { shapes, settings };
};

export const splitRemovedIds = (ids: WhiteboardRecordId[]) => {
  const recordIds: TLRecord['id'][] = [];
  const settingKeys: WhiteboardSettingKey[] = [];

  ids.forEach((id) => {
    if (!isSettingId(id)) {
      recordIds.push(id);
      return;
    }

    const key = settingKeyFromId(id);
    if (key) {
      settingKeys.push(key);
    }
  });

  return { recordIds, settingKeys };
};

export const toSettings = (parsed: ParsedSetting[]): WhiteboardSettings =>
  parsed.reduce<WhiteboardSettings>((settings, { key, value }) => {
    settings[key] = value as WhiteboardMode;

    return settings;
  }, {});

export const buildSettingRecord = (key: WhiteboardSettingKey, value: string) => ({
  id: `${SETTING_PREFIX}${key}`,
  value,
});
